// utils/axios.js
import axios from 'axios';
import { trackEvent } from './appInsights';
import {showNotification} from "./functions";
// Create an Axios instance with default settings
const instance = axios.create({
  baseURL: process.env.API_BASE_URL, // Using the environment variable 
  headers: { "Content-Type": "application/json" }, // Ensure the header is set for JSON
});

// Add request interceptor to capture start time
instance.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: performance.now() };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors here
instance.interceptors.response.use(
  (response) => {
    // Track successful API calls
    const duration = performance.now() - response.config.metadata.startTime;
    trackEvent('API_Call', {
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      status: response.status,
      duration: Math.round(duration),
      success: true
    });
    
    return response;
  },
  (error) => {
    // Track API errors
    const duration = error.config?.metadata?.startTime 
      ? performance.now() - error.config.metadata.startTime
      : 0;
    
    trackEvent('API_Error', {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      duration: Math.round(duration),
      error: error.message,
      success: false
    });

    // Optional: Handle specific status codes or errors
    // Example: Handle 401 Unauthorized
    if (error.response && [401, 403].includes(error.response.status)) {
      console.error('Authorization error:', error.response.status);
      
      // Handle deactivated account - clear token and show notification
      if (error.response.status === 403 && 
          error.response.data?.message?.toLowerCase().includes('deactivated')) {
        localStorage.removeItem("token");
        showNotification(error.response.data.message);
      }
    }
    if (error.response && [503].includes(error.response.status)) {
      showNotification()
    }
    console.error('API error:', error);
    return Promise.reject(error);
  }
);

export const externalPostApi = async (url, requestBody, options = {}) => {
  const { timeout = 120000 } = options; // Default 2 minute timeout
  try {
        const response = await axios.post(url, requestBody, { timeout });
    return response.data; // Return response data on success
  } catch (err) {
    if (err.response) {
      // Check if the error status is 503
      if (err.response.status === 503) {
        showNotification(err.response.data.message || "Service unavailable, please try again later.");
      } else {
        showNotification(err.response.data.message || "Service unavailable, please try again later.");
        console.error(`Error ${err.response.status}: ${err.response.statusText}`);
      }
    } else {
      // Handle non-HTTP errors (e.g., network issues)
      console.error("Error: ", err.message || "Unknown error occurred.");
    }
    return {}; // Return an empty object as a fallback
  }
};
export const externalOptimizeProductContent = async (requestBody) => {
  try {
    const response = await axios.post('/api/optimize_product_content', requestBody);
    return response.data; // Return response data on success
  } catch (err) {
    if (err.response) {
      // Check if the error status is 503
      if (err.response.status === 503) {
        showNotification(err.response.data.message || "Service unavailable, please try again later.");
      } else {
        // Handle other HTTP error statuses if needed
        console.error(`Error ${err.response.status}: ${err.response.statusText}`);
      }
    } else {
      // Handle non-HTTP errors (e.g., network issues)
      console.error("Error: ", err.message || "Unknown error occurred.");
    }
    return {}; // Return an empty object as a fallback
  }
};

export default instance;
